import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Grid,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  useRouter,
  PageHeader,
  PageContent,
  FormBottomBar,
  FormErrorDialog,
  useFormError,
  useNotification,
} from '@viilutalous/common';
import { departmentsAPI, employeesAPI } from './employeesAPI';

export default function DepartmentForm({ tenantId, setBreadcrumbTitle }) {
  const { navigate, params } = useRouter();
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const { error: formError, dialogOpen: errorDialogOpen, handleApiError, showError, closeDialog } = useFormError();
  const { id } = params;
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, control, setValue, trigger, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      parent: null,
      manager: null,
    },
  });

  const nameValue = watch('name');

  // Dropdown options
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    return () => {
      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(null);
      }
    };
  }, [setBreadcrumbTitle]);

  useEffect(() => {
    if (tenantId) {
      fetchDepartments();
      fetchEmployees();
      if (!isNew) {
        fetchDepartment();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll(tenantId);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeesAPI.getAll(tenantId);
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDepartment = async () => {
    try {
      setLoading(true);
      const response = await departmentsAPI.getOne(tenantId, id);
      const dept = response.data;

      setValue('name', dept.name || '');

      if (dept.parent_id) {
        setValue('parent', { id: dept.parent_id, name: dept.parent_name || '' });
      }
      if (dept.manager_id) {
        setValue('manager', { id: dept.manager_id, name: dept.manager_name || '' });
      }

      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(dept.name);
      }
    } catch (error) {
      console.error('Error fetching department:', error);
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        name: data.name.trim(),
        parent_id: data.parent?.id || null,
        manager_id: data.manager?.id || null,
      };

      if (isNew) {
        await departmentsAPI.create(tenantId, payload);
        showSuccess(t('departments.created'));
      } else {
        await departmentsAPI.update(tenantId, id, payload);
        showSuccess(t('departments.updated'));
      }
      navigate('/departments');
    } catch (error) {
      console.error('Error saving department:', error);
      handleApiError(error, t);
    } finally {
      setSaving(false);
    }
  };

  const onSubmitError = (formErrors) => {
    const errorMessages = [];
    Object.entries(formErrors).forEach(([, error]) => {
      if (error?.message) {
        errorMessages.push(error.message);
      }
    });
    if (errorMessages.length > 0) {
      showError(errorMessages.join('\n'));
    }
  };

  const handleSaveClick = async (e) => {
    if (e) e.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      onSubmitError(errors);
      return;
    }
    handleSubmit(onSubmit, onSubmitError)(e);
  };

  const handleCancel = () => {
    navigate('/departments');
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('departments.select_tenant')}</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <Paper sx={{ p: 3, maxWidth: 800 }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('departments.name')}
                {...register('name', { required: t('departments.error.name_required') })}
                error={!!errors.name}
                helperText={errors.name?.message}
                required
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="parent"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={departments.filter((dept) => dept.id !== id)}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('departments.parent')} size="small" />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="manager"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('departments.manager')} size="small" />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </PageContent>

      <FormBottomBar
        onSave={handleSaveClick}
        onCancel={handleCancel}
        saving={saving}
        disabled={!nameValue?.trim()}
      />

      <FormErrorDialog
        open={errorDialogOpen}
        onClose={closeDialog}
        message={formError?.message}
      />
    </>
  );
}
